/**
 * №22.3.2 (F1) → №29 (D-SS2) — THE ROUTE→SUBJECT+BINDING MATRIX. The committed
 * contract between a routed App-Shell surface, the SUBJECT the centre stage holds,
 * and the anchor the work rail binds to.
 *
 * ## №29 (D-SS2) — what this file became
 *
 * 22.3.2's route→anchor matrix is not re-litigated here; it is RE-HOUSED. The shell
 * grew a second question — "what is on the stage?" — and the wrong way to answer it
 * would have been a second table keyed by pathname, which is precisely the shape
 * that let the chip and the anchor disagree three times. So the columns are added to
 * the ONE table instead: every row now declares its `subject` (what the centre stage
 * renders) and its `rail` (whether the persistent work rail is present), alongside
 * the anchor it already declared. `resolveSubject()` reads the same rows as
 * `resolveSurface()`, so a subject can never drift from its binding.
 *
 * The rail's BOUND CONTEXT is the row's anchor (D-SS2: "the rail's bound context =
 * the anchor"), which is why binding is not a third column: it would be a copy of
 * one that already exists, free to rot. A run pinned into the rail overrides the
 * binding at runtime (the 22.3.1 follow/pin state machine, re-housed) — that is
 * session state, not a property of the route.
 *
 * ## №29 (S1) — the CHAT subject arrived as rows, not as an exception
 *
 * Mockup A puts the conversation on the stage. The tempting shape was one `/app/chat`
 * route plus "remember which anchor we came from" — which is the F1 defect with extra
 * steps: a surface whose declared context and applied context are different things.
 * Instead the conversation is a sub-route of the surface it belongs to
 * (`…/chat`), and its row DELEGATES every column but `subject` to the row it was
 * promoted from. See `conversationRow`.
 *
 * ## Why this file exists (22.3.2's original rationale, unchanged)
 *
 * Before it, two resolvers computed "here" from the same pathname and disagreed:
 * `routeContext()` walked the assembled sitemap (so it named every Place), while
 * `routeAnchor()` was a hand-written cascade of route LITERALS that had drifted from
 * the route table — it knew `staff/:id`, the `matter` slug, and nothing else. Every
 * unlisted surface (Contacts, invoices, fee arrangements, every Place list, every
 * ledger page) silently fell through to the workspace firehose while the bar's chip
 * happily named the Place. Because `@` drives the applied instruction/memory slice,
 * that is a WRONG-CONTEXT bug, not a label bug — and it had been re-shipped three
 * times, because "add a route" and "add its anchor" were separate acts.
 *
 * So: ONE ordered table, one row per routed surface, and one resolver
 * (`resolveSurface`) that every consumer reads — the conversation's chip, the rail's
 * binding, the anchor-stream subscription, and the chat scope + page context
 * actually posted to `/ai/chat`. A row cannot name a Place while anchoring the
 * workspace by accident: it declares BOTH, side by side, reviewably.
 *
 * A coverage gate (`scripts/test-route-anchor-matrix-22.3.2.ts`) reads the real route
 * registrations out of `App.tsx` + `workbench-registry.ts` and fails when any of them
 * resolves to the fallback row — so a new route without a matrix row cannot ship. The
 * same script renders `docs/22.3.2-route-anchor-matrix.md` and fails on drift, and the
 * hermetic journey `tests/snapshots/route-anchor-matrix-22.3.2.spec.ts` walks each row
 * in a browser asserting chip == resolved anchor == applied context.
 *
 * ## Dependency rule
 *
 * This module imports TYPES ONLY. It is imported by a plain `tsx` node script (the
 * coverage gate), so it must never reach a React component, a hook, or the DOM.
 */
import type { ChatScope, PageContext } from "../../components/chat/ChatRailContext";
import type { StreamAnchorKind } from "../../../lib/types";
// Node-importable by construction (no react, no react-router) — the coverage gate reads
// this module directly, so anything it imports must stay readable outside a browser.
import { declaredSubViews } from "./record-subviews";

/** The anchor a surface speaks at. `kind` is the engine's `StreamAnchorKind`. */
export interface RouteAnchor {
  kind: StreamAnchorKind;
  /** Agent node id · entity row id (`record`) · entity TYPE SLUG (`place`) · task id. */
  id?: string;
  /** The provisional label (the server's resolved name wins where one exists). */
  label?: string;
}

/**
 * Where the CHIP's text comes from once everything has resolved.
 *
 *  - `"node"` — the anchor is a real node (agent · record · place · task), so the
 *    SERVER's resolved name is authoritative (D-AF1: the chip is backed by the node,
 *    never a route literal). The row's label is only the first-paint placeholder.
 *  - `"surface"` — the anchor is the workspace because this surface has no node in
 *    the stream vocabulary. There is nothing for the server to resolve, so the row's
 *    own label stands — and it names the context the conversation actually gets
 *    (which is the thing `@` promises), with `note` recording why the rollup is wider.
 */
export type ChipSource = "node" | "surface";

/**
 * №29 (D-L1 / §5) — WHAT THE CENTRE STAGE HOLDS. The subject changes; the shell
 * doesn't. Every routed surface declares exactly one, from the closed set the
 * locked probe draws:
 *
 *  - `cards`   — a card list (needs-you: the decisions waiting on a human)
 *  - `table`   — a collection: a Place list, a view, a worksheet (mockup B)
 *  - `record`  — one record, with its sub-nav (a matter, a contact, a ledger)
 *  - `run`     — a workflow instance / task run in flight (mockup D)
 *  - `desk`    — a named member of staff's desk (§15.3, re-housed as a subject)
 *  - `chat`    — the conversation itself, on the stage (mockup A)
 *  - `intake`  — the two-zone New… stage; a record is born here (mockup C)
 *  - `form`    — a settings or composer surface: fields, not a collection
 */
export type SubjectKind = "cards" | "table" | "record" | "run" | "desk" | "chat" | "intake" | "form";

/**
 * №29 (S2) — THE CONVERSATION IS THE SUBJECT on these, and only these.
 *
 * Two subjects put the conversation on the stage: `chat` (mockup A — a conversation
 * about something that exists) and `intake` (mockup C — a conversation out of which
 * something is born). Everything that has to behave differently when the
 * conversation is the stage rather than the rail reads THIS, so it cannot be decided
 * twice and drift: the anchor chip (Q3 — the stage head names the anchor, so the
 * conversation stops), the launcher (a second composer over the first), the generic
 * Back bar, and the journey that asserts all three.
 */
export function conversationIsSubject(subject: SubjectKind): boolean {
  return subject === "chat" || subject === "intake";
}

/**
 * №29 (D-L1 / §5) — whether the persistent WORK RAIL is present on this surface.
 * `"work"` is the shell's normal three-zone geometry; `"none"` is the two-zone
 * variant the probe draws for New/intake (mockup C), where the stage is the whole
 * conversation and there is no crew working-state to sit beside it yet.
 */
export type RailMode = "work" | "none";

export interface ResolvedSurface {
  /** The matrix row that matched (`fallback` when nothing did). */
  key: string;
  /** The row's human name — what the committed table calls this surface. */
  surface: string;
  anchor: RouteAnchor;
  /** №29 (D-SS2) — what the centre stage holds here. */
  subject: SubjectKind;
  /** №29 (D-SS2) — whether the work rail is mounted for this subject. */
  rail: RailMode;
  /** №29 (S1) — set on a CONVERSATION row: the key of the row it is the
   *  conversation OF. See `conversationRow` for why this is one table, not two. */
  promotedFrom?: string;
  /** №29 (S2) — the row's short name in the nav's OPEN sub-nav. */
  navLabel?: string;
  /** The first-paint chip. `chipSource: "node"` means the server's name supersedes it. */
  chip: string;
  chipSource: ChipSource;
  /** The context ACTUALLY applied to a conversation started here (posted to /ai/chat). */
  scope: ChatScope;
  page: PageContext;
  placeholder: string;
  /** Why the anchor is wider than the surface, when it is. Rendered in the table. */
  note?: string;
}

/** Reads a route param. Total by construction — an absent param reads as "" rather
 *  than `undefined`, so a row never has to defend against a partial match. */
export type ParamReader = (name: string) => string;

export interface SurfaceRow {
  key: string;
  surface: string;
  /** The route pattern, absolute, in the router's own `:param` syntax. */
  pattern: string;
  /** №29 (D-SS2) — the stage's subject here. Static per route: which record is on
   *  the stage is a param, but that it IS a record is a property of the surface. */
  subject: SubjectKind;
  /** №29 (D-SS2) — three-zone (`"work"`) or the two-zone intake variant (`"none"`). */
  rail: RailMode;
  /** №29 (S1) — a conversation row points at the subject row it was promoted from. */
  promotedFrom?: string;
  /**
   * №29 (S2) — the row's name in the nav's OPEN sub-nav, in the USER's words.
   *
   * A row's `surface` string is the committed table's name for it ("A record's full
   * process view"), written for a reviewer — putting it on a nav row would be S0's
   * defect 1 all over again. So a row that is a SUB-VIEW of an open record declares
   * its own short label, and only rows that declare one appear in the sub-nav. That
   * is also what keeps the dead-nav ban true by construction: the sub-nav is
   * generated from rows that are REGISTERED ROUTES (the coverage gate proves they
   * are), so it cannot contain a row that goes nowhere.
   */
  navLabel?: string;
  note?: string;
  resolve: (param: ParamReader) => Omit<ResolvedSurface, "key" | "surface" | "note" | "subject" | "rail" | "promotedFrom">;
}

// ---------------------------------------------------------------------------
// Shared label helpers — mirrored from the server so the first paint matches what
// the anchor stream will return (the same mirror contract as `defaultActorForAnchor`).
// ---------------------------------------------------------------------------

/** Mirror of the engine's `placeDisplayName`: "matter" → "Matters",
 *  "register-entry" → "Register entries". Asserted against the server in R5. */
export function placeLabel(typeSlug: string): string {
  const words = typeSlug.replace(/-/g, " ");
  const plural = /[^aeiou]y$/i.test(words)
    ? `${words.slice(0, -1)}ies`
    : /(s|x|z|ch|sh)$/i.test(words)
      ? `${words}es`
      : `${words}s`;
  return plural.charAt(0).toUpperCase() + plural.slice(1);
}

/** The chip shown while a node's name is still resolving (never a route literal). */
const RESOLVING = "…";

const ACT_PLACEHOLDER = "Ask or act — everything runs through the same verbs and approvals";
const RECORD_PLACEHOLDER = "Ask or act on this record — everything runs through the same verbs and approvals";

/** A `record` surface: one entity row, whatever its type. The type comes from the
 *  route's `:entity` param — NEVER a hardcoded slug (that was the F1 root cause). */
function recordSurface(entity: string, id: string) {
  return {
    anchor: { kind: "record" as const, id, label: id },
    chip: id,
    chipSource: "node" as const,
    // `type_slug` (not `label`) carries the record's TYPE: the transport sends
    // {kind,id,type_slug} and drops `label`, so the type would otherwise never
    // reach the prompt — the chip would name a record the model can't classify.
    scope: { kind: "page", id, type_slug: entity, label: entity } as ChatScope,
    page: { kind: "page", id, name: id } as PageContext,
    placeholder: RECORD_PLACEHOLDER,
  };
}

/** A `place` surface: the collection of one entity type. */
function placeSurface(typeSlug: string, placeholder?: string) {
  const label = placeLabel(typeSlug);
  return {
    anchor: { kind: "place" as const, id: typeSlug, label },
    chip: label,
    chipSource: "node" as const,
    scope: { kind: "page", id: typeSlug, type_slug: typeSlug, label: typeSlug } as ChatScope,
    page: { kind: "page", id: typeSlug, name: label } as PageContext,
    placeholder: placeholder ?? `Ask or act on ${label.toLowerCase()}`,
  };
}

/** A surface with no node in the anchor vocabulary: the rollup is the workspace
 *  firehose, and the chip names the context the conversation is actually given. */
function workspaceSurface(chip: string, scope: ChatScope, page: PageContext, placeholder: string) {
  return { anchor: { kind: "workspace" as const, label: "Everywhere" }, chip, chipSource: "surface" as const, scope, page, placeholder };
}

// ---------------------------------------------------------------------------
// THE MATRIX. One row per routed App-Shell surface. Specificity (most static
// segments wins) picks between rows, so the order here is documentation, not logic.
// ---------------------------------------------------------------------------

/** The SUBJECT rows — every surface whose stage holds something other than the
 *  conversation. The conversation rows below are generated FROM these. */
const SUBJECT_ROWS: SurfaceRow[] = [
  {
    key: "needs-you",
    surface: "Needs you (home)",
    pattern: "/app",
    subject: "cards",
    rail: "work",
    resolve: () =>
      workspaceSurface(
        "Everywhere",
        { kind: "page", label: "needs-you" },
        { kind: "page", name: "Needs you" },
        ACT_PLACEHOLDER,
      ),
  },

  // №29 (S2) — INTAKE (mockup C). The probe's only two-zone surface: no work rail,
  // because nothing is running yet. It anchors on the WORKSPACE and says so — a
  // record that does not exist cannot be anchored on, and giving intake a
  // `chipSource: "node"` anchor for a record it is about to propose would be the F1
  // defect with a new URL. It gets no conversation row: it IS the conversation.
  {
    key: "intake",
    surface: "New — a record is born from the conversation",
    pattern: "/app/new",
    subject: "intake",
    rail: "none",
    note: "Nothing exists yet, so there is nothing to anchor on: the rollup is the workspace and the chip says so. The two-zone geometry is the probe's (mockup C) — no crew working-state exists for work that has not started.",
    resolve: () =>
      workspaceSurface(
        "New",
        { kind: "page", label: "intake" },
        { kind: "page", name: "New" },
        "Describe what you need — a new matter, a contact, a piece of work",
      ),
  },

  // №53 (53.2) — WORKFORCE, THE BOARD. It anchors on the WORKSPACE, not on any
  // one hire: the board's subject is the crew as a whole, and anchoring it on a
  // hire would make the rail's bound context disagree with what the stage shows
  // the moment the reader is looking at five of them.
  //
  // ⚠ This row was MISSING until 53.4. `/app/workforce` shipped in 53.2 with no
  // matrix row, so the gate had been red on `route has a matrix row` ever since
  // — which is the F1 defect class the matrix exists to prevent: the surface
  // resolved through FALLBACK_ROW to the workspace firehose while the bar's chip
  // named the Place. It landed on the right anchor by accident, not by
  // declaration, and an accident is not a contract.
  {
    key: "workforce",
    surface: "Workforce (the board)",
    pattern: "/app/workforce",
    subject: "cards",
    rail: "work",
    note: "The crew as a whole is the subject, so the anchor is the workspace. A per-hire anchor would contradict a stage showing five of them.",
    resolve: () =>
      workspaceSurface(
        "Workforce",
        { kind: "page", label: "workforce" },
        { kind: "page", name: "Workforce" },
        ACT_PLACEHOLDER,
      ),
  },

  // №53 (53.4) — HANDLED, THE RECEIPT. The day across every hire, so the anchor
  // is the workspace for the same reason Workforce's is: the stage holds many
  // hires' work at once and a single-hire anchor would be a claim the page
  // contradicts on its own first band.
  {
    key: "handled",
    surface: "Handled (the receipt)",
    pattern: "/app/handled",
    subject: "cards",
    rail: "work",
    note: "Every hire's verbs for one day, refusals included. Workspace-anchored: the page is the day, and a day belongs to no single hire.",
    resolve: () =>
      workspaceSurface(
        "Handled",
        { kind: "page", label: "handled" },
        { kind: "page", name: "Handled" },
        ACT_PLACEHOLDER,
      ),
  },

  // ── the Staff axis ──────────────────────────────────────────────────────────
  {
    key: "staff-desk",
    surface: "A hire's desk",
    pattern: "/app/staff/:agentId",
    subject: "desk",
    rail: "work",
    resolve: (param) => ({
      anchor: { kind: "agent", id: param("agentId"), label: RESOLVING },
      chip: RESOLVING,
      chipSource: "node",
      scope: { kind: "page", id: param("agentId"), label: "staff" },
      page: { kind: "page", id: param("agentId"), name: "Staff desk" },
      placeholder: "Ask or act on this desk — everything runs through the same verbs and approvals",
    }),
  },
  {
    key: "task-thread",
    surface: "A task's run thread",
    pattern: "/app/task/:taskId",
    subject: "run",
    rail: "work",
    resolve: (param) => ({
      anchor: { kind: "task", id: param("taskId"), label: RESOLVING },
      chip: RESOLVING,
      chipSource: "node",
      scope: { kind: "task", id: param("taskId") },
      page: { kind: "page", id: param("taskId"), name: "Task thread" },
      placeholder: "Ask or act on this run — everything runs through the same verbs and approvals",
    }),
  },

  // ── declared Places (Stage 3 renderers) ─────────────────────────────────────
  {
    key: "place-list",
    surface: "A Place list (any entity type)",
    pattern: "/app/place/:entity",
    subject: "table",
    rail: "work",
    resolve: (param) => placeSurface(param("entity")),
  },
  {
    key: "place-record",
    surface: "A record (any entity type)",
    pattern: "/app/place/:entity/:id",
    subject: "record",
    rail: "work",
    // №53 · 53.3 — `Record` → `Stream`. The tab's SLOT is unchanged, which is the
    // point: the stream takes the landing view rather than adding a ninth tab
    // ("no change to the tab budget", `docs/53-mockups/matters-1.png` caption).
    navLabel: "Stream",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  // №29 (S4, §3 ④) — the record's OWN views, as routes. These were `useState` tabs on
  // the matter cockpit until S4; the removal list's ④ is "tab-chrome on records that the
  // left-rail sub-nav replaces", and a sub-nav can only carry a view that is addressable.
  //
  // The patterns are generic (`:entity`), NOT one per type × view: which types offer
  // which sub-view is a DECLARATION (`record-subviews.ts`), not a routing question —
  // the same lesson S2 recorded when it retired `place/matter/:id` in favour of the
  // renderer registry. A URL naming a sub-view the type does not declare redirects to
  // the record rather than rendering a frame with nothing behind it.
  //
  // Billing and Trust are deliberately two rows, not one "Money" (see record-subviews.ts).
  {
    key: "place-record-parties",
    surface: "A record's parties",
    pattern: "/app/place/:entity/:id/parties",
    subject: "record",
    rail: "work",
    navLabel: "Parties",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  {
    key: "place-record-billing",
    surface: "A record's billing (office money)",
    pattern: "/app/place/:entity/:id/billing",
    subject: "record",
    rail: "work",
    navLabel: "Billing",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  {
    key: "place-record-trust",
    surface: "A record's trust position (statutory, distinct from billing)",
    pattern: "/app/place/:entity/:id/trust",
    subject: "record",
    rail: "work",
    navLabel: "Trust",
    note: "Never folded into Billing. Trust money is held for someone else under statutory certification; office money is the firm's. One nav row over both would put a regulated ledger and a revenue view behind the same word.",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  {
    key: "place-record-register",
    surface: "A record's registers",
    pattern: "/app/place/:entity/:id/register",
    subject: "record",
    rail: "work",
    navLabel: "Register",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  {
    key: "place-record-conflicts",
    surface: "A record's conflict checks",
    pattern: "/app/place/:entity/:id/conflicts",
    subject: "record",
    rail: "work",
    navLabel: "Conflicts",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  {
    key: "place-record-files",
    surface: "A record's files",
    pattern: "/app/place/:entity/:id/files",
    subject: "record",
    rail: "work",
    navLabel: "Files",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  // №53.3.1 S0 (E-33, D-CR26) — the record's declared sections, evicted from the
  // landing view into their own last tab. Same generic `:entity` pattern as every
  // other sub-view row (record-subviews.ts decides which types declare it).
  {
    key: "place-record-details",
    surface: "A record's declared sections (key dates, team, effective rates, …)",
    pattern: "/app/place/:entity/:id/details",
    subject: "record",
    rail: "work",
    navLabel: "Details",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  {
    key: "place-process",
    surface: "A record's full process view",
    pattern: "/app/place/:entity/:id/process",
    subject: "run",
    rail: "work",
    navLabel: "Process",
    note: "The process runs ON the record, and the route carries the record id (not the workflow-instance id) — so the anchor is the record. The `workflow` kind stays reachable via MCP for an instance-keyed rollup.",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },
  {
    key: "place-program",
    surface: "A record's program (journey) view",
    pattern: "/app/place/:entity/:id/program",
    subject: "run",
    rail: "work",
    navLabel: "Program",
    note: "As `place-process` — a journey across chained instances anchored on the record it runs on.",
    resolve: (param) => recordSurface(param("entity"), param("id")),
  },

  // ── workbench Places (lenses over a collection) ─────────────────────────────
  {
    key: "clients",
    surface: "Clients (lens over contacts)",
    pattern: "/app/clients",
    subject: "table",
    rail: "work",
    note: "Clients is a LENS over the one `contact` master (§13.2.2) — contacts holding the client role. Its anchor is therefore @Contacts; the role filter is not expressible as an anchor.",
    resolve: () => placeSurface("contact"),
  },
  {
    key: "registers",
    surface: "Registers",
    pattern: "/app/registers",
    subject: "table",
    rail: "work",
    resolve: () => placeSurface("register-entry"),
  },
  {
    key: "invoices",
    surface: "Invoices",
    pattern: "/app/billing/invoices",
    subject: "table",
    rail: "work",
    resolve: () => placeSurface("invoice"),
  },
  {
    key: "vendor-invoices",
    surface: "Vendor invoices",
    pattern: "/app/billing/vendor-invoices",
    subject: "table",
    rail: "work",
    resolve: () => placeSurface("vendor-invoice"),
  },
  {
    key: "time-day",
    surface: "Day view / timesheet",
    pattern: "/app/time/day",
    subject: "table",
    rail: "work",
    resolve: () => placeSurface("time-entry", "Ask or act on today's time"),
  },
  {
    key: "wip-worksheet",
    surface: "WIP & billing worksheet",
    pattern: "/app/billing/wip",
    subject: "table",
    rail: "work",
    note: "A cross-matter worksheet over DERIVED, unbilled WIP (never a stored collection) — no single node to anchor on, so the rollup is the workspace.",
    resolve: () =>
      workspaceSurface(
        "WIP & billing",
        { kind: "page", label: "wip-billing" },
        { kind: "page", name: "WIP & billing worksheet" },
        "Ask or act on unbilled WIP",
      ),
  },
  // ── Settings: the console, absorbed (№53 53.0, D-CR14 / D-CR21) ─────────────
  //
  // Every row below is `subject: "form"` / `rail: "work"` and anchors on the
  // WORKSPACE: configuration is not a collection and has no node to anchor on, so
  // asking a question on a settings surface is a question about the firm.
  //
  // `firm-settings` at `/app/settings/firm` is UNCHANGED and is already an
  // app-native page (`FirmSettingsPage`), registered through the WORKBENCH REGISTRY
  // rather than `App.tsx` — which is why a grep of the route file alone does not
  // find it. It is the destination the Settings index's "Firm settings" row opens;
  // nothing about it is re-parented, because it never lived on the maker surface.
  {
    key: "settings",
    surface: "Settings",
    pattern: "/app/settings",
    subject: "form",
    rail: "work",
    note: "The settings index — three groups by owner (the firm · the workspace · you).",
    resolve: () =>
      workspaceSurface(
        "Settings",
        { kind: "page", label: "settings" },
        { kind: "page", name: "Settings" },
        "Ask about this firm's settings",
      ),
  },
  {
    key: "firm-settings",
    surface: "Firm settings",
    pattern: "/app/settings/firm",
    subject: "form",
    rail: "work",
    note: "Configuration, not a collection — no node to anchor on.",
    resolve: () =>
      workspaceSurface(
        "Firm settings",
        { kind: "page", label: "firm-settings" },
        { kind: "page", name: "Firm settings" },
        "Ask about the firm's settings",
      ),
  },

  {
    key: "settings-workspace",
    surface: "Workspace settings",
    pattern: "/app/settings/workspace",
    subject: "form",
    rail: "work",
    note: "Workspace-level configuration (name, theme, protection). Re-parented from the maker surface (D-CR21) — same component, inside the shell.",
    resolve: () =>
      workspaceSurface(
        "Workspace settings",
        { kind: "page", label: "settings-workspace" },
        { kind: "page", name: "Workspace settings" },
        "Ask about this workspace's settings",
      ),
  },
  {
    key: "settings-members",
    surface: "People & roles",
    pattern: "/app/settings/members",
    subject: "form",
    rail: "work",
    note: "Who is here and what they can reach. Re-parented from the maker surface (D-CR21) — same component, inside the shell.",
    resolve: () =>
      workspaceSurface(
        "People & roles",
        { kind: "page", label: "settings-members" },
        { kind: "page", name: "People & roles" },
        "Ask about who is in this firm",
      ),
  },
  {
    key: "settings-connections",
    surface: "Connections & equipment",
    pattern: "/app/settings/connections",
    subject: "form",
    rail: "work",
    note: "What the crew can reach, and how. Re-parented from the maker surface (D-CR21) — same component, inside the shell.",
    resolve: () =>
      workspaceSurface(
        "Connections & equipment",
        { kind: "page", label: "settings-connections" },
        { kind: "page", name: "Connections & equipment" },
        "Ask about this firm's connections",
      ),
  },
  {
    key: "settings-tables",
    surface: "Tables & types",
    pattern: "/app/settings/tables",
    subject: "form",
    rail: "work",
    note: "The records this firm keeps. Re-parented from the maker surface (D-CR21) — same component, inside the shell.",
    resolve: () =>
      workspaceSurface(
        "Tables & types",
        { kind: "page", label: "settings-tables" },
        { kind: "page", name: "Tables & types" },
        "Ask about this firm's records",
      ),
  },
  {
    key: "settings-workflows",
    surface: "Workflows",
    pattern: "/app/settings/workflows",
    subject: "form",
    rail: "work",
    note: "How matters are carried. Re-parented from the maker surface (D-CR21) — same component, inside the shell.",
    resolve: () =>
      workspaceSurface(
        "Workflows",
        { kind: "page", label: "settings-workflows" },
        { kind: "page", name: "Workflows" },
        "Ask about this firm's workflows",
      ),
  },
  {
    key: "settings-endpoints",
    surface: "Endpoints",
    pattern: "/app/settings/endpoints",
    subject: "form",
    rail: "work",
    note: "Where hosts connect in. Re-parented from the maker surface (D-CR21) — same component, inside the shell.",
    resolve: () =>
      workspaceSurface(
        "Endpoints",
        { kind: "page", label: "settings-endpoints" },
        { kind: "page", name: "Endpoints" },
        "Ask about this firm's endpoints",
      ),
  },
  {
    key: "settings-library",
    surface: "Library",
    pattern: "/app/settings/library",
    subject: "form",
    rail: "work",
    note: "Pages, memories and prompts. Re-parented from the maker surface (D-CR21) — same component, inside the shell.",
    resolve: () =>
      workspaceSurface(
        "Library",
        { kind: "page", label: "settings-library" },
        { kind: "page", name: "Library" },
        "Ask about this firm's library",
      ),
  },

  // ── the trust ledger ────────────────────────────────────────────────────────
  // A ledger is not a node in the anchor vocabulary (it is not an entity row and has
  // no library node), so these rows anchor at the workspace and say so. The chip and
  // the applied context still name the ledger — that is what the conversation gets.
  {
    key: "trust-list",
    surface: "Trust (ledger list)",
    pattern: "/app/trust",
    subject: "table",
    rail: "work",
    note: "A trust ledger is not an entity row or a library node, so it has no stream anchor. Giving `ledger` its own anchor kind is a table row away — deliberately out of №22.3.2's scope.",
    resolve: () =>
      workspaceSurface(
        "Trust",
        { kind: "page", label: "trust" },
        { kind: "page", name: "Trust ledgers" },
        "Ask about the trust ledgers",
      ),
  },
  // №29 (D-SS2) — the ledger surfaces are RECORD subjects with one exception: the
  // composer is a form (fields you fill, not a record you read). The subject column
  // is per-row here for that reason, not folded into the generator's tuple by accident.
  ...([
    ["ledger-cockpit", "Ledger cockpit", "/app/trust/:ledgerId", "record"],
    ["ledger-account", "Ledger account view", "/app/trust/:ledgerId/accounts/:accountId", "record"],
    ["ledger-reconciliation", "Ledger reconciliation", "/app/trust/:ledgerId/reconciliation", "record"],
    ["ledger-period-close", "Ledger period close", "/app/trust/:ledgerId/period-close", "record"],
    ["ledger-period-history", "Ledger period history", "/app/trust/:ledgerId/period-history", "record"],
    ["ledger-composer", "Ledger composer", "/app/trust/:ledgerId/compose", "form"],
  ] as const).map(([key, surface, pattern, subject]): SurfaceRow => ({
    key,
    surface,
    pattern,
    subject,
    rail: "work",
    note: "See `trust-list` — a ledger has no anchor node; the ledger context is applied via `page`.",
    resolve: (param) =>
      workspaceSurface(
        "Trust ledger",
        { kind: "page", id: param("ledgerId"), label: "ledger" },
        { kind: "ledger", id: param("ledgerId"), name: "Trust ledger" },
        "Ask or act on this ledger — e.g. draft a payment to counsel",
      ),
  })),
];

// ---------------------------------------------------------------------------
// №29 (S1) — THE CONVERSATION ROWS (mockup A: "chat is the subject").
//
// The probe's own argument for the three-zone shell ends on this line: *"the
// conversation and the artefact sit side by side and one of them is the subject.
// Make which-one-is-subject a user choice, not an architectural one."* So the chat
// subject is not a separate destination with its own idea of "here" — it is the
// SAME surface with the conversation moved from the rail onto the stage, and the
// route says so: `…/chat` under the route it was promoted from.
//
// Each row therefore DELEGATES its resolve to the row named in `promotedFrom`. It
// does not restate the anchor, the chat scope or the page context — it cannot
// disagree with them, because it does not have its own copy. That is the whole of
// the invariant the gate then asserts out loud:
//
//   **promoting the conversation to the stage is a geometry change, not a context
//   change.** Same anchor, same rollup, same instruction slice — a different chair.
//
// Which surfaces get one: exactly those whose anchor survives the move. The
// ledger pages, the WIP worksheet and firm settings anchor at the workspace with a
// `chipSource: "surface"` label of their own (see their notes), so promoting them
// would silently swap "@ Trust ledger" for "@ Everywhere" — a narrower context
// wearing the same words. They get no conversation row, and therefore no promote
// control, rather than one that quietly lies (the dead-nav ban, applied to an
// affordance instead of a nav row).
// ---------------------------------------------------------------------------

function subjectRow(key: string): SurfaceRow {
  const row = SUBJECT_ROWS.find((r) => r.key === key);
  if (!row) throw new Error(`route-anchor-matrix: no subject row "${key}" to promote from`);
  return row;
}

/** A conversation row: `pattern` is the source route + `/chat`, and every column
 *  except the subject comes from the source row itself. */
function conversationRow(key: string, surface: string, pattern: string, promotedFrom: string): SurfaceRow {
  const source = subjectRow(promotedFrom);
  return {
    key,
    surface,
    pattern,
    subject: "chat",
    rail: "work",
    promotedFrom,
    // The source's `note` comes with it. A note explains why a row's anchor is wider
    // than what it names, and that reason is a property of the ANCHOR — which the
    // conversation shares. Letting it drop would leave the promoted row silently
    // unexplained on exactly the surfaces that most need explaining.
    ...(source.note ? { note: source.note } : {}),
    resolve: (param) => source.resolve(param),
  };
}

const CONVERSATION_ROWS: SurfaceRow[] = [
  conversationRow("chat-workspace", "The conversation, on the stage (workspace)", "/app/chat", "needs-you"),
  // №29 (S2) — the row S1 could not ship. `/app/place/:entity/chat` scores 34 in
  // React Router's ranking (1 + 10 "app" + 10 "place" + 3 `:entity` + 10 "chat"),
  // which was an exact TIE with the type-baked record routes `place/matter/:id`,
  // `place/invoice/:id` and `place/fee-arrangement/:id` (1 + 10 + 10 + 10 + 3). A
  // tie breaks on registration order, so `/app/place/matter/chat` rendered the
  // matter cockpit looking for a matter called "chat" while `/app/place/contact/chat`
  // worked — a control that is right on some Places and silently wrong on others,
  // which S1 declined to ship. S2 retired those three routes (the renderer is now a
  // registry read on the ONE record route — `record-renderers.ts`), so the tie is
  // gone and the row is safe. The gate asserts BOTH halves: the routes are absent
  // AND this row exists.
  conversationRow("chat-place", "The conversation, on the stage (a Place list)", "/app/place/:entity/chat", "place-list"),
  // №51 (D-CH6, 2026-08-18) — this row carried `navLabel: "Chat"` and was the ONLY
  // conversation row that did. That label was the record sub-nav's Chat tab, and David
  // ruled the tab dies: the docked panel is on screen at all times, so a tab that
  // navigates to the same conversation spends one of the record's ten slots on a surface
  // the user already has. Dropping the label is the second of the two locks (the first is
  // `chat-record` leaving `record-subviews.ts`) because `openRecordNav` requires BOTH —
  // and a label whose only reader was that nav is no longer "a name written for a user".
  // The ROW STAYS: the route is registered, `conversationRouteFor` finds it by
  // `promotedFrom` for the stage head, the work rail and ⌘K, and the 22.3.2 matrix gate
  // still asserts it posts the same `/ai/chat` body as `place-record`.
  conversationRow("chat-record", "The conversation, on the stage (a record)", "/app/place/:entity/:id/chat", "place-record"),
  conversationRow("chat-desk", "The conversation, on the stage (a hire's desk)", "/app/staff/:agentId/chat", "staff-desk"),
];

export const ROUTE_ANCHOR_MATRIX: SurfaceRow[] = [...SUBJECT_ROWS, ...CONVERSATION_ROWS];

/** The row used when nothing matched. The coverage gate asserts no REGISTERED route
 *  ever lands here — it exists only for an unrecognised URL a user typed. */
export const FALLBACK_ROW: SurfaceRow = {
  key: "fallback",
  surface: "Unrecognised route",
  pattern: "*",
  // №29 — an unrecognised URL renders the needs-you home, so it holds the same
  // subject that home does. The shell never has to guess a geometry.
  subject: "cards",
  rail: "work",
  resolve: () =>
    workspaceSurface(
      "Everywhere",
      { kind: "page", label: "needs-you" },
      { kind: "page", name: "Needs you" },
      ACT_PLACEHOLDER,
    ),
};

// ---------------------------------------------------------------------------
// Matching. A tiny, dependency-free matcher (this module must stay node-importable):
// equal segment count, static segments must be equal, `:param` captures. Ties are
// broken by specificity (more static segments wins), so row ORDER never decides.
// ---------------------------------------------------------------------------

function matchPattern(pattern: string, pathname: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const s = pathname.split("/").filter(Boolean);
  if (p.length !== s.length) return null;
  const params: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    const seg = p[i]!;
    if (seg.startsWith(":")) {
      if (!s[i]) return null;
      params[seg.slice(1)] = decodeURIComponent(s[i]!);
    } else if (seg !== s[i]) {
      return null;
    }
  }
  return params;
}

function staticness(pattern: string): number {
  return pattern.split("/").filter((seg) => seg && !seg.startsWith(":")).length;
}

/** The matched row and the params it captured. One matcher, two readers: the
 *  resolver below, and the promote/demote pair (which needs the params to build
 *  the sibling route). */
function matchRow(pathname: string): { row: SurfaceRow; params: Record<string, string> } {
  const path = pathname.replace(/[?#].*$/, "").replace(/\/+$/, "") || "/";
  let best: { row: SurfaceRow; params: Record<string, string> } | null = null;
  for (const row of ROUTE_ANCHOR_MATRIX) {
    const params = matchPattern(row.pattern, path);
    if (!params) continue;
    if (!best || staticness(row.pattern) > staticness(best.row.pattern)) best = { row, params };
  }
  return best ?? { row: FALLBACK_ROW, params: {} };
}

/** The ONE resolver. Every consumer of "here" — chip, anchor, chat scope, page
 *  context — reads this, so they cannot disagree. */
export function resolveSurface(pathname: string): ResolvedSurface {
  const { row, params } = matchRow(pathname);
  const param: ParamReader = (name) => params[name] ?? "";
  return {
    key: row.key,
    surface: row.surface,
    subject: row.subject,
    rail: row.rail,
    ...(row.promotedFrom ? { promotedFrom: row.promotedFrom } : {}),
    ...(row.navLabel ? { navLabel: row.navLabel } : {}),
    ...(row.note ? { note: row.note } : {}),
    ...row.resolve(param),
  };
}

/** Substitute a matched param set back into a pattern. Returns null when the
 *  pattern needs a param this path never captured — which is how a promote target
 *  that could not be built honestly ends up as "no control" rather than a bad URL. */
function fillPattern(pattern: string, params: Record<string, string>): string | null {
  const out: string[] = [];
  for (const seg of pattern.split("/")) {
    if (!seg.startsWith(":")) { out.push(seg); continue; }
    const v = params[seg.slice(1)];
    if (!v) return null;
    out.push(encodeURIComponent(v));
  }
  return out.join("/");
}

/**
 * №29 (S1) — WHERE THE CONVERSATION GOES WHEN IT BECOMES THE SUBJECT, and back.
 *
 * `conversationRouteFor` is the promote target for a subject surface; `subjectRouteFor`
 * is the demote target for a conversation surface. Both are derived from the ONE
 * table via `promotedFrom`, so a control can never point somewhere the matrix has
 * not declared — and a surface with no conversation row simply has no control.
 */
export function conversationRouteFor(pathname: string): string | null {
  const { row, params } = matchRow(pathname);
  if (row.subject === "chat") return null;
  const chat = ROUTE_ANCHOR_MATRIX.find((r) => r.promotedFrom === row.key);
  return chat ? fillPattern(chat.pattern, params) : null;
}

export function subjectRouteFor(pathname: string): string | null {
  const { row, params } = matchRow(pathname);
  if (!row.promotedFrom) return null;
  return fillPattern(subjectRow(row.promotedFrom).pattern, params);
}

/**
 * №29 (D-SS2) — the SUBJECT ROUTER. What the centre stage holds for a pathname, and
 * whether the work rail is mounted beside it.
 *
 * Deliberately a thin read of `resolveSurface` rather than its own matcher: the
 * subject and the rail's binding come off the SAME row, so the stage can never show
 * one thing while the rail binds to another. The shell calls this once, above the
 * routed outlet; nothing downstream re-derives a geometry from the pathname.
 */
export interface ResolvedSubject {
  /** The matrix row that matched. */
  key: string;
  subject: SubjectKind;
  rail: RailMode;
  /** What the rail binds to when this subject is on stage (D-SS2). */
  binding: RouteAnchor;
}

export function resolveSubject(pathname: string): ResolvedSubject {
  const s = resolveSurface(pathname);
  return { key: s.key, subject: s.subject, rail: s.rail, binding: s.anchor };
}

/**
 * №29 (S2) — THE OPEN RECORD'S SUB-NAV (the probe's `OPEN` section).
 *
 * Every mockup that has a record open draws it in the left nav with its own
 * sub-views under it — `Chat · Record · Files · Tables · Tasks · Money · People`.
 * Ours is generated from THIS table rather than hand-kept, for the reason the table
 * exists at all: a hand-kept nav list and a route registration are two acts, and the
 * gap between them is where dead nav comes from. A row appears here only if it
 *
 *   ① is a registered route (the coverage gate asserts every row is), and
 *   ② is a sub-view of the record currently open — its pattern is the record
 *      pattern, optionally plus one segment, and
 *   ③ declares a `navLabel` — a name written for a user, not the review label.
 *
 * `null` when the pathname is not a record or one of its sub-views, and then the nav
 * renders no OPEN section at all rather than an empty one.
 */
export interface OpenRecordNav {
  /** The record's own id, as the nav row's label (the server-resolved name wins in
   *  the chip; this is the stable identifier the nav shows). */
  id: string;
  entity: string;
  /** The sub-views, in table order, each already a concrete path. */
  items: { key: string; label: string; path: string }[];
}

export function openRecordNav(pathname: string): OpenRecordNav | null {
  const { row, params } = matchRow(pathname);
  const RECORD = "/app/place/:entity/:id";
  const isRecordFamily = row.pattern === RECORD || row.pattern.startsWith(`${RECORD}/`);
  if (!isRecordFamily) return null;
  const id = params.id;
  const entity = params.entity;
  if (!id || !entity) return null;
  // №29 (S4, §3 ④) — the sub-views this ENTITY TYPE declares, in the order it declares
  // them. S2 walked every row in the record family instead, which was entity-blind: a
  // contact was offered a matter's views. The declaration is the seam №23 will fill from
  // the server (see `record-subviews.ts`); this function does not know or care where the
  // answer comes from.
  //
  // A declared key still has to survive the two S2 rules — it must be a REGISTERED route
  // (the coverage gate asserts every matrix row is) and it must carry a `navLabel`, a
  // name written for a user rather than the row's review label. So a declaration cannot
  // conjure a row: it can only choose among rows that already go somewhere.
  const byKey = new Map(ROUTE_ANCHOR_MATRIX.map((r) => [r.key, r]));
  const items: OpenRecordNav["items"] = [];
  for (const key of declaredSubViews(entity)) {
    const r = byKey.get(key);
    if (!r || !r.navLabel) continue;
    if (r.pattern !== RECORD && !r.pattern.startsWith(`${RECORD}/`)) continue;
    const path = fillPattern(r.pattern, params);
    if (path) items.push({ key: r.key, label: r.navLabel, path });
  }
  return { id, entity, items };
}

/** Expand a route pattern into a concrete pathname — used by the coverage gate and
 *  the CI journey so both walk the SAME surfaces the router registers. */
export function samplePath(pattern: string, sample: Record<string, string> = {}): string {
  return pattern
    .split("/")
    .map((seg) => (seg.startsWith(":") ? (sample[seg.slice(1)] ?? `${seg.slice(1)}-sample`) : seg))
    .join("/");
}
